import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { boardRingColor, holdHands } from './roles';
import { HandMark } from '../HandMark';
import {
  holdPosition,
  holdKey,
  holdWidth,
  isScannedHold,
  isShapedWall,
  outlineFrame,
  scannedOutline,
} from './geometry';
import { ScannedHoldOutlines, type ScannedHoldOutline } from './ScannedHoldOutlines';
import { ZoomResetButton } from '../photo-editor/ZoomResetButton';
import RepeatedHoldDialog from './RepeatedHoldDialog';
import { cellName } from './useCellName';
import type { BoardGridController } from './useBoardGrid';

interface BoardGridCanvasProps {
  grid: BoardGridController;
  imageUrl: string;
  /**
   * Something drawn over the wall but not clipped by it — the panel that acts on
   * the hold in hand, which stands at that hold and has to be free to hang off
   * the edge of the picture. Placed in a box the same size as the wall, so
   * whatever goes in it can be positioned in the wall's own fractions.
   */
  overlay?: ReactNode;
  /**
   * The tallest the board may be drawn, as a CSS length. Applied as a **width**
   * worked out from the photo's proportions: the holds sit in percentages of
   * this box, so capping the height any other way would leave the box wider than
   * the picture and every hold beside the wall it belongs to.
   *
   * Wanted when the board has to share the screen with something else — a
   * circuit's list of holds, which is read against the wall it describes.
   */
  maxHeight?: string;
  /**
   * Take the height of whatever the board is put in rather than the photo's own
   * — a landscape board opened out to the height of a phone's screen. The photo
   * then covers the frame and is panned across sideways (see `useZoomPan`), and
   * `maxHeight` has nothing left to cap.
   */
  fill?: boolean;
}

/** How much of itself a hold keeps when the circuit is focused elsewhere. */
const DIMMED_OPACITY = 0.3;

/**
 * How an unpainted cell is drawn. Faint on purpose: on a dense wall there are
 * several hundred of them and the setter is reading the photo underneath to
 * pick the holds out — they need to be findable, not to be the picture. What
 * the setter has painted keeps its full colour and a thicker ring, so the
 * problem stands out from the wall it is set on.
 */
/** How thick a painted hold's ring is; an empty cell's is a hairline. */
const PAINTED_RING_PX = 3;
const EMPTY_HOLD_BORDER = 'rgba(255,255,255,0.35)';
const EMPTY_HOLD_FILL = 'rgba(0,0,0,0.08)';

/**
 * The wall itself: the board photo with a hold drawn over every cell that
 * carries one, painted in the colour the toolbar has in hand.
 *
 * Which holds there are, how big each is drawn, and what each can be comes from
 * the layout when it has one: a Kilter mixes bolt-ons with screw-on foot chips a
 * fraction of the size and leaves two thirds of its lattice empty, so drawing
 * every cell the same would be a grid that looks nothing like the wall. A chip
 * too small to pull on paints a foot whatever colour is selected — see
 * roleForHold. Without a hold map it falls back to the plain grid, every cell of
 * which is a hold that takes any role.
 *
 * In circuit mode the taps mean something else: every tap is the next hold of
 * the run — bar a foot, which is stood on throughout rather than moved to, so it
 * joins the problem without taking a place in the order. A tap on a hold the run
 * already uses is put to the setter (see RepeatedHoldDialog): it is either the use
 * they mean to work on or the climber coming back to that hold, and one circle per
 * cell cannot say which. Coming back needs a hold in between — two uses side by
 * side are a **match**, which is a role, and a second press on the hold in hand
 * steps it in and out of one. Reordering the run, and the hands that follow from
 * it, belong to the list over the board — see CircuitList.
 */
export default function BoardGridCanvas({
  grid,
  imageUrl,
  maxHeight,
  overlay,
  fill = false,
}: BoardGridCanvasProps) {
  const { t } = useTranslation();
  const {
    rows,
    columns,
    calibration,
    extraHolds,
    shapes,
    scanned,
    holdRadii,
    circuit,
    zoom,
    aspect,
    uses,
    focusedCell,
    cells,
    gridWidth,
    paint,
    pendingTap,
    pickPendingUse,
    repeatPendingHold,
    matchPendingHold,
    cancelPendingTap,
    setAspect,
    selected,
  } = grid;

  // A scanned wall's holds are drawn as the outlines the scan traced; the
  // button over each is the target, see-through, sized to the hold.
  const outlines: ScannedHoldOutline[] = [];

  const buttons = cells.map(({ row, col, hold: spec }) => {
    const traced = isScannedHold(row) ? scannedOutline(scanned, col) : null;
    const point = traced
      ? outlineFrame(traced).centre
      : holdPosition(calibration, rows, columns, extraHolds, row, col, shapes, scanned);
    const key = holdKey({ row, col });
    const use = uses.get(key);
    const role = use?.hold.role;
    // The ring and the fill: the role, or the hand the wall lights the hold in.
    const ring = use ? boardRingColor(use.hold) : null;
    // The hand(s) that take the hold, marked on its own side of the ring.
    const hands = use ? holdHands(use.hold) : [];
    // Nothing pointed at leaves the whole circuit lit; a hold in focus is the
    // one the board dims down to. A cell the problem stands on is never dimmed
    // for it: the foot is there for the whole climb, so it is shown for the
    // whole climb.
    const dimmed =
      circuit && !!use && !use.standing && focusedCell !== null && focusedCell !== key;
    const width = traced ? Math.max(outlineFrame(traced).width, 0.01) : holdWidth(spec, holdRadii, gridWidth);
    const cell = cellName(t, { row, col }, isShapedWall(shapes));
    const isSelected = selected?.row === row && selected?.col === col;
    if (traced) outlines.push({ key, outline: traced, color: ring, dimmed, selected: isSelected });

    return (
      <button
        key={key}
        type="button"
        // The hand is in the label rather than inside the circle: it is already
        // painted onto the hold, and a hold on a phone has room for its number
        // or for a letter, not for both.
        aria-label={
          use?.hold.hand ? `${cell} — ${t(`climbs.board.hands.${use.hold.hand}`)}` : cell
        }
        onClick={() => paint(row, col)}
        aria-pressed={selected?.row === row && selected?.col === col ? true : undefined}
        data-hands={hands.length > 0 ? hands.join(',') : undefined}
        className={`absolute flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border transition-transform active:scale-90 ${
          selected?.row === row && selected?.col === col
            ? 'ring-2 ring-white ring-offset-1 ring-offset-black/40'
            : ''
        }`}
        style={{
          left: `${point.x * 100}%`,
          top: `${point.y * 100}%`,
          width: `${width * 100}%`,
          aspectRatio: '1 / 1',
          // The ring and the fill are the role — or, on a circuit that forces
          // hands, the hand the wall lights the hold in (see boardRingColor) —
          // and that hand is marked on its own side of the ring as well, see
          // HandMark below.
          // The outline is the hold on a scanned wall; the circle is only where
          // it is pressed.
          borderColor: traced ? 'transparent' : (ring ?? EMPTY_HOLD_BORDER),
          borderWidth: traced ? 0 : role ? PAINTED_RING_PX : 1,
          backgroundColor: traced ? 'transparent' : ring ? `${ring}55` : EMPTY_HOLD_FILL,
          opacity: dimmed ? DIMMED_OPACITY : 1,
        }}
      >
        <HandMark hands={hands} ring={PAINTED_RING_PX} />
        {/* The number sits inside the circle, which is a few percent of the
            photo's width — 18px on a phone, so the type is small enough for two
            digits to stay inside the hold. A hold the circuit comes back to —
            or matches on, which is the same hold twice over — wears both of its
            numbers ("5,20"), with the type stepping down to keep them there. A
            foot has none: the run passes over it rather than stopping on it,
            and a foot chip has no room for a number anyway. */}
        {circuit && use?.label && spec?.kind !== 'foot' && (
          <span
            className={`whitespace-nowrap font-bold leading-none text-white ${
              use.label.length > 2 ? 'text-[7px]' : 'text-[9px]'
            }`}
            style={{ textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}
          >
            {use.label}
          </span>
        )}
      </button>
    );
  });

  return (
    // Two boxes of exactly the same size: the wall, which clips whatever is
    // dragged about inside it, and this one, which does not — so a panel
    // anchored to a hold near the edge is not cut in half by the picture it
    // belongs to. Everything laid out in fractions works in either.
    //
    // Unframed: the photo runs the full width of whatever it is put in, so a
    // border or rounded corners would read as an inset. A capped board narrows
    // instead and centres itself in the room it gives up.
    <div
      className={`relative mx-auto w-full ${fill ? 'h-full' : ''}`}
      style={!fill && maxHeight && aspect ? { maxWidth: `calc(${maxHeight} * ${aspect})` } : undefined}
    >
      <div
        {...zoom.frameProps}
        className={`relative w-full select-none overflow-hidden bg-muted ${fill ? 'h-full' : ''}`}
      >
        {/* The photo and the holds scale as one thing: both are laid out in the
            same box, so a single transform cannot pull them apart. */}
        <div ref={zoom.contentRef} {...zoom.layerProps}>
          <img
            src={imageUrl}
            alt=""
            className="block w-full"
            draggable={false}
            onLoad={(e) =>
              setAspect(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight || null)
            }
          />
          <ScannedHoldOutlines holds={outlines} />
          {buttons}
        </div>

        {/* The way back out of a zoomed board, in the corner it is wanted in — the
            row of controls above says what is being painted, not how closely the
            wall is being read. */}
        <ZoomResetButton zoom={zoom} label={t('climbs.board.zoom.reset')} />

        {/* What a tap on a hold the circuit already uses meant. It hangs off the
            board rather than off either surface, so the phone form and the
            gym-admin panel — which both draw their wall with this — ask it the
            same way. */}
        <RepeatedHoldDialog
          tap={pendingTap}
          onPick={pickPendingUse}
          onRepeat={repeatPendingHold}
          onMatch={matchPendingHold}
          onCancel={cancelPendingTap}
          shaped={isShapedWall(shapes)}
        />
      </div>

      {overlay}
    </div>
  );
}
